import { get, has, size, isEmpty } from 'lodash';
import React from 'react';
import styled from 'styled-components';

import ClientLog, {
  StyledTimeline as CommunicationTimeline,
} from '../ClientLog';
import TicketLog from '../TicketLog';
import LogListSkeleton from '../LogListSkeleton';
import LogSkeleton from '../LogSkeleton';
import InfiniteList from '../../../../../InfiniteList';
import {
  LOG_RELATIONS,
  TICKET_LOG_TYPES,
  CLIENT_LOG_TYPES,
} from '../../../../../../constants';

import ITicketLog from '../../interfaces/ITicketLog';
import IClientLog from '../../interfaces/IClientLog';
import { ValueOf } from '@utils/tsUtils';

const PAGE_SIZE_DEFAULT = 20;
const THRESHOLD_DEFAULT = 5;

export interface Props {
  className?: string;
  logs: IClientLog[] | ITicketLog[];
  hasNextPage: boolean;
  logsMeta: {
    page: number;
    size: number;
    total: number;
    totalPages: number;
  };
  isNextPageLoading: boolean;
  selectedCallId: string | null;
  threshold: number;
  minimumBatchSize: number;
  relateTo: (typeof LOG_RELATIONS)[keyof typeof LOG_RELATIONS];
  onClickIcon: (params: {
    id: string;
    type:
      | (typeof CLIENT_LOG_TYPES)[keyof typeof CLIENT_LOG_TYPES]
      | (typeof TICKET_LOG_TYPES)[keyof typeof TICKET_LOG_TYPES];
    isBot: boolean;
    ticketId: string;
  }) => void;
  onDelete(params: { id: string }): void;
  onEdit(params: { id: string }): void;
  onLoadRequest(page: number): void;
  onPin(params: {
    id: string;
    isPinned: boolean;
    relateTo: (typeof LOG_RELATIONS)[keyof typeof LOG_RELATIONS];
  }): void;
  onFlag(params: {
    id: string;
    isFlagged: boolean;
    relateTo: (typeof LOG_RELATIONS)[keyof typeof LOG_RELATIONS];
  }): void;
  onReasonDelete(params: { callId: string; [propName: string]: unknown }): void;
  onReasonSubmit(params: { callId: string; [propName: string]: unknown }): void;
  toggleTicketLogsGroup(params: { id: string }): void;
  onDeleteAttachment(params: {
    attachId: string;
    noteId: string;
    relateTo: ValueOf<typeof LOG_RELATIONS>;
  }): void;
}

interface State {
  expandedSummaryCallId: string | null;
}

export const getLogsWithCallSummaryState = (
  logs: Array<IClientLog | ITicketLog>,
  relateTo: (typeof LOG_RELATIONS)[keyof typeof LOG_RELATIONS],
  selectedCallId: string | null | undefined,
  expandedSummaryCallId: string | null
) =>
  (logs || []).map(log => {
    const callId = get(log, 'call.id');
    const isSelectedCall =
      relateTo === LOG_RELATIONS.CLIENT &&
      (selectedCallId == null || callId === selectedCallId);

    return {
      ...log,
      isCallSummaryExpanded:
        isSelectedCall &&
        expandedSummaryCallId !== null &&
        callId === expandedSummaryCallId,
    };
  });

const defaultProps: Partial<Props> = {
  logs: [],
  hasNextPage: false,
  isNextPageLoading: false,
  threshold: THRESHOLD_DEFAULT,
  minimumBatchSize: PAGE_SIZE_DEFAULT,
};

class LogList extends React.PureComponent<Props, State> {
  private listRef: React.RefObject<HTMLDivElement>;

  renderCount: number;

  static defaultProps = defaultProps as Partial<Props>;

  static getItemKey(index, logs) {
    return get(logs, `${index}.id`) || index;
  }

  constructor(props: Props) {
    super(props);

    this.state = {
      expandedSummaryCallId: null,
    };
    this.listRef = React.createRef();
    this.renderCount = 0;

    this.handleCallSummaryToggle = this.handleCallSummaryToggle.bind(this);
    this.handleScrollLock = this.handleScrollLock.bind(this);
    this.handleLoadPage = this.handleLoadPage.bind(this);
    this.isItemLoaded = this.isItemLoaded.bind(this);
  }

  componentDidUpdate(prevProps: Props) {
    const { selectedCallId } = this.props;
    const { expandedSummaryCallId } = this.state;

    if (
      prevProps.selectedCallId !== selectedCallId &&
      expandedSummaryCallId !== null
    ) {
      this.setState({ expandedSummaryCallId: null });
    }
  }

  handleCallSummaryToggle(callId: string) {
    this.setState(({ expandedSummaryCallId }) => ({
      expandedSummaryCallId: expandedSummaryCallId === callId ? null : callId,
    }));
  }

  handleScrollLock({ communicationBottom, preventScroll }) {
    const listElement = this.listRef.current;
    if (listElement) {
      listElement.style.overflow = preventScroll ? 'hidden' : 'overlay';
      const { bottom: listBottom } = listElement.getBoundingClientRect();
      const elDiff = listBottom - communicationBottom;
      const bottomOffset = 10;

      if (elDiff < 0) {
        listElement.scrollTop += Math.abs(elDiff) + bottomOffset;
      }
    }
  }

  handleLoadPage() {
    const { isNextPageLoading } = this.props;
    if (isNextPageLoading) {
      return;
    }

    const { onLoadRequest, logsMeta } = this.props;
    onLoadRequest(logsMeta.page + 1);
  }

  getItemCount() {
    const { logs, hasNextPage } = this.props;
    return hasNextPage ? size(logs) + 1 : size(logs);
  }

  isItemLoaded(index) {
    const { logs } = this.props;
    return has(logs, `${index}.id`) || has(logs, `${index}`);
  }

  emptyRenderer() {
    const { isNextPageLoading } = this.props;

    return isNextPageLoading ? (
      <StyledLogListSkeleton />
    ) : (
      <StyledEmpty>Нет логов.</StyledEmpty>
    );
  }

  render() {
    const {
      logs,
      relateTo,
      hasNextPage,
      selectedCallId,
      onEdit,
      onPin,
      onFlag,
      onDelete,
      onClickIcon,
      onLoadRequest,
      onReasonDelete,
      onReasonSubmit,
      threshold,
      logsMeta,
      toggleTicketLogsGroup,
      onDeleteAttachment,
      ...props
    } = this.props;
    const { expandedSummaryCallId } = this.state;
    const measuredLogs =
      relateTo === LOG_RELATIONS.CLIENT
        ? getLogsWithCallSummaryState(
            logs,
            relateTo,
            selectedCallId,
            expandedSummaryCallId
          )
        : logs;

    this.renderCount += 1;

    return (
      <StyledLogList {...props}>
        {hasNextPage || !isEmpty(logs) ? (
          <StyledList
            flexible
            isItemLoaded={this.isItemLoaded}
            itemCount={this.getItemCount()}
            itemData={measuredLogs}
            itemKey={LogList.getItemKey}
            onLoadRequest={this.handleLoadPage}
            outerRef={this.listRef}
            threshold={threshold}
          >
            {({ data, index, style }) => {
              if (!this.isItemLoaded(index)) {
                return (
                  <StyledItem style={style}>
                    <StyledLogSkeleton />
                  </StyledItem>
                );
              }

              const log = data[index];
              const { type } = log;

              return (
                <StyledItem
                  key={log.id}
                  data-id={log.id}
                  style={style}
                  type={type}
                >
                  {LOG_RELATIONS.CLIENT === relateTo && (
                    <ClientLog
                      {...log}
                      selectedCallId={selectedCallId}
                      onDelete={onDelete}
                      onEdit={onEdit}
                      onPin={onPin}
                      onClickIcon={onClickIcon}
                      onClickOutside={this.handleScrollLock}
                      onCallSummaryToggle={this.handleCallSummaryToggle}
                      onReasonDelete={onReasonDelete}
                      onReasonSubmit={onReasonSubmit}
                      onDeleteAttachment={onDeleteAttachment}
                    />
                  )}
                  {LOG_RELATIONS.TICKET === relateTo && (
                    <TicketLog
                      {...log}
                      onPin={onPin}
                      onFlag={onFlag}
                      onEdit={onEdit}
                      onClickIcon={onClickIcon}
                      onClickOutside={this.handleScrollLock}
                      toggleTicketLogsGroup={toggleTicketLogsGroup}
                      onDeleteAttachment={onDeleteAttachment}
                    />
                  )}
                </StyledItem>
              );
            }}
          </StyledList>
        ) : (
          this.emptyRenderer()
        )}
      </StyledLogList>
    );
  }
}

const StyledLogList = styled.ul`
  background-color: #e6e6e6;
  height: 100%;
  overflow: hidden;
  width: 100%;
`;

const StyledList = styled(InfiniteList)<{
  [index: string]: unknown;
}>`
  height: 100%;
  overflow: auto;
`;

const StyledItem = styled.li<{
  [index: string]: unknown;
}>`
  width: calc(100% - 5px);
  :first-of-type ${CommunicationTimeline}:before {
    top: 13px;
    height: calc(100% - 13px);
  }

  :last-of-type ${CommunicationTimeline}:before {
    height: 9px;
  }
`;

const StyledLogListSkeleton = styled(LogListSkeleton)`
  height: 100%;
  margin-left: 8px;
  margin-top: 13px;
`;

const StyledLogSkeleton = styled(LogSkeleton)`
  margin-left: 8px;
  margin-top: 5px;
`;

const StyledEmpty = styled.div`
  color: #4d4d4d;
  height: 100%;
  padding-top: 85px;
  text-align: center;
`;

export default LogList;
